/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlaybackContentType, SpeedLevel } from "../enums";
import { SPEED_VALUE } from "../trickplay";

// ── Playback Category ─────────────────────────────────────────────────────────

/**
 * Simplified two-category classification for forward/backward cache ratio
 * computation on 10-foot platforms.
 *
 * LIVE    — Live TV with time-shift buffer (wall-clock labels on progress bar).
 * VOD     — All non-live content: StartOver, CatchUp, SOCU, NDVR, VOD
 *           (0 → program duration progress bar format).
 */
export type PlaybackCategory = "LIVE" | "VOD";

// ── Configuration Interface ───────────────────────────────────────────────────

/**
 * All configurable constants for the 10-foot sliding-window thumbnail strategy.
 * Every field is externalized so device-specific overrides can be applied at
 * runtime (e.g. reduced MAX_CACHE_SIZE on low-memory platforms).
 */
export interface T10FootThumbnailConfig {
  /**
   * Weight applied to the base ratio when blending with the position-zone ratio.
   * finalFwd = weight × baseForward + (1 − weight) × positionZoneFwd.
   * Default: 0.5 (equal blend).
   */
  BASE_FORWARD_RATIO_WEIGHT: number;

  /**
   * Delay (ms) after both PlayerState.LOADED and PlayerState.FIRST_FRAME fire
   * before the initial fetch begins.  Avoids competing with stream startup.
   * Default: 2000.
   */
  DEBOUNCE_START_DELAY_MS: number;

  /** Thumbnails to pre-fetch per D-pad seek direction batch. Default: 25. */
  DIRECTIONAL_PREFETCH_BATCH: number;

  /** Initial fetch window from current position (seconds). Default: 600 (~10 min). */
  INITIAL_PRELOAD_DURATION_SEC: number;

  /**
   * Cadence (seconds) of the live-edge continuous fetch timer.
   * Should equal thumbnailInterval (2 × MEDIA_SEGMENT_DURATION).
   * Default: 12.
   */
  LIVE_CONTINUOUS_FETCH_INTERVAL_SEC: number;

  /**
   * Maximum neighbor positions (in thumbnail-interval steps) searched when the
   * exact seek target is not cached.  3 positions = 36 s at 12 s interval.
   * Default: 3.
   */
  MAX_ACCEPTABLE_GAP: number;

  /** Hard cache ceiling (thumbnail count). Default: 300 (~1 hr at 12s interval). */
  MAX_CACHE_SIZE: number;

  /**
   * Fraction of MAX_CACHE_SIZE spanned by the eviction-protected window.
   *
   * Must be < 1.  A window as wide as the cap guarantees zero out-of-window
   * eviction candidates whenever the cache is well-centred on the playhead —
   * which starved batch eviction down to the one-at-a-time hard-cap fallback
   * exactly during trick-play, when up to TRICKPLAY_MAX_PARALLEL_REQUESTS
   * fetches are landing per pass.  Reserving a margin keeps a permanent supply
   * of candidates so the cap is enforceable in batches.
   *
   * Default: 0.8 (at MAX_CACHE_SIZE 300 this leaves ~60 always-evictable slots).
   */
  PROTECTED_WINDOW_RATIO: number;

  /** Maximum concurrent HTTP requests during normal playback. Default: 4. */
  MAX_PARALLEL_REQUESTS: number;

  /**
   * Maximum concurrent HTTP requests while trick-play is active.  Default: 8.
   *
   * Higher than MAX_PARALLEL_REQUESTS because in Thumbnail trick-play mode the
   * player is paused at the seek entry point for the whole scrub session (see
   * ThumbnailTrickplayControlImpl), so the video pipeline is idle and the
   * bandwidth MAX_PARALLEL_REQUESTS is being conservative about is unused.
   * Reverts to MAX_PARALLEL_REQUESTS the moment the scrub ends.
   *
   * Not raised further: gains flatten above ~6 on HTTP/1.1 per-origin
   * connection limits, and simultaneous blob decode plus peak memory is the
   * real constraint on low-end TV chipsets.
   */
  TRICKPLAY_MAX_PARALLEL_REQUESTS: number;

  /**
   * Number of entries evicted per pass while trick-play is active.  Default: 25
   * (matches DIRECTIONAL_PREFETCH_BATCH).  Outside trick-play eviction stays
   * one-at-a-time.
   *
   * Batching creates cache headroom so the next ~N completions skip the
   * eviction scan entirely, instead of every completion paying a full
   * MAX_CACHE_SIZE-wide pass.  Clamped at runtime relative to MAX_CACHE_SIZE —
   * see ThumbnailManager.resolve10FootEvictionBatchSize().
   */
  EVICTION_BATCH_SIZE: number;

  /**
   * Fraction of the active-side look-ahead batch the virtual position must
   * cross before the next directional batch is scheduled (PRD §3.3.3).
   * Default: 0.7.  Replaces the previous "extend on every tick" behaviour that
   * let the look-ahead frontier race away from (or stall behind) the user.
   */
  LOOK_AHEAD_THRESHOLD: number;

  /**
   * positionRatio above which LIVE playback is considered "near the live edge"
   * and biases the base ratio toward backward (PRD §3.2.2). Default: 0.85.
   */
  LIVE_EDGE_RATIO_THRESHOLD: number;

  /**
   * Fraction (± ) added to the active seek direction's share of each batch so
   * the user-facing side stays denser (PRD §3.2.3). Default: 0.10 (±10%).
   */
  DIRECTION_BIAS_DELTA: number;

  /** Lower clamp for either side's ratio after bias (PRD §3.2.3). Default: 0.10. */
  RATIO_CLAMP_MIN: number;

  /** Upper clamp for either side's ratio after bias (PRD §3.2.3). Default: 0.90. */
  RATIO_CLAMP_MAX: number;

  /**
   * Seconds of WALL-CLOCK look-ahead to maintain during a speed session.
   *
   * The right invariant is real time, not frame count: a fixed batch of 25
   * frames is 100 s of wall-clock look-ahead at L1 (3x) but only 5 s at L3
   * (60x), so a frame-count batch is simultaneously wasteful at low speed and
   * inadequate at high speed.  Default: 20.
   *
   * Not smaller: the look-ahead has to cover request latency plus the interval
   * between extension passes, and a value of 8 left L3 with 8 s of cover — one
   * slow round-trip from an empty window and the preview falls back to the
   * last-known-good frame.
   */
  LOOK_AHEAD_WALL_SECONDS: number;

  /**
   * Floor on the directional batch size regardless of speed, so a very low
   * speed still queues enough to absorb request latency.  Default: 10.
   *
   * This floor is what sets the L1 window: at 3x the wall-clock target asks
   * for only 5 frames, and 6 (the previous floor) gave 72 content-seconds =
   * 24 s of viewing, against the 300 s / 100 s the pre-speed-adaptive code
   * fetched.  10 frames restores 120 content-seconds = 40 s of L1 cover
   * without reinstating the fixed 25-frame batch at every speed.
   */
  MIN_DIRECTIONAL_PREFETCH_BATCH: number;

  /**
   * Prefetch grid stride, in thumbnail intervals, per speed level.
   *
   * The virtual seek target only advances once per TICK_INTERVAL_MS (1 s) at
   * every speed level, so at most one new frame per second can ever be
   * displayed.  At 60x the playhead crosses five 12-second frames per second,
   * so fetching every frame means four in five are decoded and evicted without
   * ever being seen.  Striding the PREFETCH grid (never the lookup grid) drops
   * that waste directly.
   *
   * Every strided offset is still a real frame — the CDN filename convention
   * places one at each mStartTime + k*12, and a stride selects the k ≡ 0 (mod
   * S) sub-lattice.  The nearest-neighbour probe absorbs the gap, which caps
   * the usable stride at 2*MAX_ACCEPTABLE_GAP + 1; see
   * ThumbnailManager.resolve10FootStride().
   *
   * Default: L1 1 (dense), L2 2, L3 5.
   */
  SPEED_GRID_STRIDE: Record<SpeedLevel, number>;

  /**
   * Maximum concurrent requests per speed level while trick-play is active.
   *
   * Deliberately NOT monotonic — L3 gets fewer slots than L2. Eight concurrent
   * blob decodes at 60x is precisely the peak that trips the low-memory
   * watchdog, and with the L3 stride only ~1 frame per second is actually
   * needed, so a lower ceiling costs nothing and halves peak simultaneous
   * blob-plus-decode footprint.
   *
   * Default: L1 4, L2 6, L3 4.
   */
  SPEED_MAX_PARALLEL: Record<SpeedLevel, number>;

  /**
   * Ceiling on the pending-fetch queue depth.
   *
   * The queue holds numbers, so it is not itself the memory problem — but it
   * is deduplicated only against the content range, so hammering the skip key
   * can pile up offsets far behind the playhead that will each be fetched,
   * blobbed and immediately evicted. Refusing bulk look-ahead beyond this
   * depth removes that churn. Priority entries (the seek target, the live
   * edge) are never refused; they displace from the tail instead.
   *
   * Clamped at runtime to at least the initial preload width plus one
   * directional batch — see ThumbnailManager.enable10FootStrategy().
   * Default: 120.
   */
  MAX_PENDING_QUEUE: number;

  /**
   * Minimum gap (ms) between full directional batch rebuilds in onSeekStarted.
   *
   * Short-press skip fires on KEYUP, so an `event.repeat` guard cannot help —
   * hammering the key produces genuinely distinct presses, each of which must
   * still move the seek target. Only the *prefetch batch* can be coalesced,
   * never the seek itself.
   *
   * 150 ms sits well below LONG_PRESS_THRESHOLD_MS (400) so it cannot
   * interfere with hold detection, below one 250 ms trick-play poll tick, and
   * at roughly the ceiling of sustained human key-tap cadence on a remote.
   * Default: 150.
   */
  SEEK_BATCH_COALESCE_MS: number;
}

// ── Default Configuration ─────────────────────────────────────────────────────

/**
 * Maximum gap (in thumbnail positions) when searching for the nearest loaded
 * thumbnail.  3 positions = 36 seconds.
 *
 * Declared as a standalone constant, not just a field of DEFAULT_10FOOT_CONFIG,
 * because the browser eager-fetch path needs the same value but has no runtime
 * config object to read it from.  Previously each path carried its own literal
 * 3, which is two sources of truth for one grid invariant.
 */
export const DEFAULT_MAX_ACCEPTABLE_GAP: number = 3;

/**
 * Default configuration for the 10-foot sliding-window thumbnail strategy.
 * All values are overridable at runtime via enable10FootStrategy().
 */
export const DEFAULT_10FOOT_CONFIG: T10FootThumbnailConfig = {
  BASE_FORWARD_RATIO_WEIGHT: 0.5,
  DEBOUNCE_START_DELAY_MS: 2000,
  DIRECTIONAL_PREFETCH_BATCH: 25,
  INITIAL_PRELOAD_DURATION_SEC: 600,
  LIVE_CONTINUOUS_FETCH_INTERVAL_SEC: 12,
  MAX_ACCEPTABLE_GAP: DEFAULT_MAX_ACCEPTABLE_GAP,
  MAX_CACHE_SIZE: 300,
  PROTECTED_WINDOW_RATIO: 0.8,
  MAX_PARALLEL_REQUESTS: 4,
  TRICKPLAY_MAX_PARALLEL_REQUESTS: 8,
  EVICTION_BATCH_SIZE: 25,
  LOOK_AHEAD_THRESHOLD: 0.7,
  LIVE_EDGE_RATIO_THRESHOLD: 0.85,
  DIRECTION_BIAS_DELTA: 0.1,
  RATIO_CLAMP_MIN: 0.1,
  RATIO_CLAMP_MAX: 0.9,
  LOOK_AHEAD_WALL_SECONDS: 20,
  MIN_DIRECTIONAL_PREFETCH_BATCH: 10,
  SPEED_GRID_STRIDE: {
    [SpeedLevel.L1]: 1,
    [SpeedLevel.L2]: 2,
    [SpeedLevel.L3]: 5
  },
  SPEED_MAX_PARALLEL: {
    [SpeedLevel.L1]: 4,
    [SpeedLevel.L2]: 6,
    [SpeedLevel.L3]: 4
  },
  MAX_PENDING_QUEUE: 120,
  SEEK_BATCH_COALESCE_MS: 150
};

/**
 * Overrides applied on memory-constrained 10-foot devices.
 *
 * Merged over DEFAULT_10FOOT_CONFIG by enable10FootStrategy() when
 * getDeviceMemoryTier() reports "low". DEFAULT_10FOOT_CONFIG keeps its
 * existing values as the standard tier, so capable devices — and every
 * existing test, which calls enable10FootStrategy() with no argument — are
 * completely unaffected.
 *
 * These are starting values, expected to be tuned on a real constrained unit
 * using the ThumbnailManager.getMemoryDiagnostics() session summary. The two
 * numbers to watch are outstanding blob URLs (native memory held) and the
 * fetches-to-delivered ratio (wasted work).
 *
 * Sizing rationale:
 *   MAX_CACHE_SIZE 120        120 x 12 s = 24 minutes of coverage, far more
 *                             than any single hold session traverses, at 40%
 *                             of the standard blob footprint.
 *   INITIAL_PRELOAD unchanged The preload width is not what constrains memory
 *                             — MAX_CACHE_SIZE is, and it is enforced against
 *                             everything the preload fetches. Halving it here
 *                             only made the initial window run out sooner (5 s
 *                             of viewing at L3), which is precisely when a gap
 *                             in the look-ahead becomes visible. 600 s is 50
 *                             entries, comfortably inside the 120 cap.
 *   TRICKPLAY_MAX_PARALLEL 4  Halves the peak count of blobs being fetched and
 *                             decoded simultaneously, which the config comment
 *                             on that field already identifies as the real
 *                             constraint on low-end TV chipsets.
 *   EVICTION_BATCH_SIZE 12    Matches the runtime clamp of MAX_CACHE_SIZE / 10
 *                             so the effective value is visible here rather
 *                             than silently reduced.
 */
export const LOW_MEMORY_10FOOT_OVERRIDES: Partial<T10FootThumbnailConfig> = {
  MAX_CACHE_SIZE: 120,
  MAX_PARALLEL_REQUESTS: 3,
  TRICKPLAY_MAX_PARALLEL_REQUESTS: 4,
  DIRECTIONAL_PREFETCH_BATCH: 12,
  EVICTION_BATCH_SIZE: 12,
  // 50 preload entries + one 12-entry directional batch. Stated explicitly
  // rather than left to enable10FootStrategy()'s runtime clamp, which would
  // raise a smaller literal to exactly this and make the configured value a
  // fiction.
  MAX_PENDING_QUEUE: 62,
  SPEED_MAX_PARALLEL: {
    [SpeedLevel.L1]: 3,
    [SpeedLevel.L2]: 4,
    [SpeedLevel.L3]: 3
  }
};

// ── PlaybackCategory Mapping ──────────────────────────────────────────────────

/**
 * Map a PlaybackContentType to the two-category classification used for
 * forward/backward cache ratio computation.
 *
 * Returns null only for unknown/future PlaybackContentType values as a safety
 * fallback — the caller treats null as "bypass 10-foot path".
 *
 * Mapping rationale:
 *   LIVE      → "LIVE"  (time-shift buffer; position is behind live edge)
 *   All others → "VOD"  (StartOver, CatchUp, SOCU, NDVR, VOD all share
 *                         0-to-duration progress bar format)
 *
 * @param type - The PlaybackContentType from the active playback session.
 * @returns The simplified PlaybackCategory, or null if the type is unrecognised.
 */
export function mapToPlaybackCategory(
  type: PlaybackContentType
): PlaybackCategory | null {
  switch (type) {
    case PlaybackContentType.LIVE:
      return "LIVE";
    case PlaybackContentType.STARTOVER_BEGINNING:
    case PlaybackContentType.STARTOVER_RESUME:
    case PlaybackContentType.CATCHUP_BEGINNING:
    case PlaybackContentType.CATCHUP_RESUME:
    case PlaybackContentType.NDVR_LIVE:
    case PlaybackContentType.NDVR_RECORDED:
    case PlaybackContentType.VOD:
      return "VOD";
    default:
      return null;
  }
}

// ── Window Ratio Computation ──────────────────────────────────────────────────

/**
 * Compute the forward/backward cache split percentages for the sliding window.
 *
 * Pure function — no side effects.  Both `forwardPct` and `backwardPct` are
 * returned as floats in [0, 1]; the caller is responsible for rounding to
 * integer slot counts.
 *
 * Algorithm (two-step blend):
 *
 *   Step 1 — Base ratio by category:
 *     LIVE  (positionRatio > 0.85, near live edge) → forward 30%, backward 70%
 *     LIVE  (positionRatio ≤ 0.85, mid-buffer)    → forward 40%, backward 60%
 *     VOD   (all positions)                         → forward 70%, backward 30%
 *
 *   Step 2 — Position-zone override (applied to both categories):
 *     positionRatio < 0.15               → forward 85%, backward 15%
 *     positionRatio 0.15 – 0.40          → forward 70%, backward 30%
 *     positionRatio 0.40 – 0.60          → forward 60%, backward 40%
 *     positionRatio 0.60 – 0.85          → forward 40%, backward 60%
 *     positionRatio > 0.85               → forward 20%, backward 80%
 *
 *   Blend:
 *     finalFwd = weight × baseForward + (1 − weight) × positionZoneFwd
 *     backwardPct = 1 − forwardPct
 *
 * @param category      - PlaybackCategory ("LIVE" | "VOD").
 * @param positionRatio - Current position as a ratio of seekable range [0, 1].
 * @param weight        - Blend weight for base ratio (default 0.5 = equal blend).
 * @returns Object with `forwardPct` and `backwardPct` both in [0, 1] summing to 1.
 */
export function compute10FootWindowRatio(
  category: PlaybackCategory,
  positionRatio: number,
  weight: number = DEFAULT_10FOOT_CONFIG.BASE_FORWARD_RATIO_WEIGHT,
  directionBias: number = 0
): { forwardPct: number; backwardPct: number } {
  // Clamp positionRatio to [0, 1].
  let ratio = positionRatio;
  if (ratio < 0) {
    ratio = 0;
  } else if (ratio > 1) {
    ratio = 1;
  }

  // Step 1: Base ratio by category.
  let baseForward: number;
  if (category === "LIVE") {
    baseForward =
      ratio > DEFAULT_10FOOT_CONFIG.LIVE_EDGE_RATIO_THRESHOLD ? 0.3 : 0.4;
  } else {
    // VOD: StartOver, CatchUp, SOCU, NDVR, VOD
    baseForward = 0.7;
  }

  // Step 2: Position-zone ratio.
  let zoneForward: number;
  if (ratio < 0.15) {
    zoneForward = 0.85;
  } else if (ratio < 0.4) {
    zoneForward = 0.7;
  } else if (ratio < 0.6) {
    zoneForward = 0.6;
  } else if (ratio <= 0.85) {
    zoneForward = 0.4;
  } else {
    zoneForward = 0.2;
  }

  // Blend.
  let blendWeight = weight;
  if (blendWeight < 0) {
    blendWeight = 0;
  } else if (blendWeight > 1) {
    blendWeight = 1;
  }
  let forwardPct = blendWeight * baseForward + (1 - blendWeight) * zoneForward;

  // Step 3 — Active-direction bias (PRD §3.2.3): shift the split toward the
  // current seek direction (+delta forward / −delta backward), then clamp both
  // sides to [RATIO_CLAMP_MIN, RATIO_CLAMP_MAX].  directionBias defaults to 0
  // (no active seek) so non-directional callers are unaffected.
  forwardPct = forwardPct + directionBias;
  if (forwardPct < DEFAULT_10FOOT_CONFIG.RATIO_CLAMP_MIN) {
    forwardPct = DEFAULT_10FOOT_CONFIG.RATIO_CLAMP_MIN;
  } else if (forwardPct > DEFAULT_10FOOT_CONFIG.RATIO_CLAMP_MAX) {
    forwardPct = DEFAULT_10FOOT_CONFIG.RATIO_CLAMP_MAX;
  }

  return { forwardPct, backwardPct: 1 - forwardPct };
}

// ── Speed-Adaptive Tuning Resolvers ───────────────────────────────────────────

/**
 * Divisor bounding the trick-play eviction batch relative to MAX_CACHE_SIZE, so
 * a small configured cache is never gutted by a single pass.  At the default
 * cache of 300 this permits the configured batch of 25; at a low-memory or test
 * cap of 5–12 it collapses the batch back to 1.
 */
const EVICTION_BATCH_CAP_DIVISOR: number = 10;

/**
 * Concurrency ceiling for the current mode: the elevated trick-play limit
 * while a scrub is active, otherwise the normal-playback limit.
 *
 * Safe to raise during trick-play because Thumbnail mode leaves the player
 * paused at the seek entry point for the whole session, so the video pipeline
 * is idle and the bandwidth MAX_PARALLEL_REQUESTS is conservative about is
 * unused.  When a scrub ends mid-flight the active-request count can exceed the
 * lowered limit; the caller's free-slot arithmetic then goes negative and it
 * simply waits for the in-flight requests to complete — concurrency
 * self-corrects without needing to abort anything.
 *
 * Note L3 gets FEWER slots than L2 by design: 8 concurrent decodes at 60× is
 * the peak that trips the low-memory watchdog on constrained TVs.
 *
 * @param config             - Active 10-foot configuration.
 * @param speedLevel         - Active speed level, or null outside a speed session.
 * @param isTrickPlayActive  - Whether a scrub is currently in progress.
 */
export function resolveMaxParallelRequests(
  config: T10FootThumbnailConfig,
  speedLevel: SpeedLevel | null,
  isTrickPlayActive: boolean
): number {
  if (!isTrickPlayActive) {
    return config.MAX_PARALLEL_REQUESTS;
  }
  if (speedLevel === null) {
    // Discrete skip — no sustained speed, so the elevated ceiling applies.
    return config.TRICKPLAY_MAX_PARALLEL_REQUESTS;
  }
  return config.SPEED_MAX_PARALLEL[speedLevel];
}

/**
 * Prefetch grid stride in thumbnail intervals for the active speed level.
 *
 * Clamped to 2 * MAX_ACCEPTABLE_GAP + 1. That bound is load-bearing: with
 * stride S the fine-grid snap of an arbitrary display target sits at most
 * floor(S/2) steps from the nearest strided frame, and the nearest-neighbour
 * probe in the lookup path only searches MAX_ACCEPTABLE_GAP steps either way.
 * Exceed it and every lookup misses.
 *
 * @param config     - Active 10-foot configuration.
 * @param speedLevel - Active speed level, or null outside a speed session.
 */
export function resolveGridStride(
  config: T10FootThumbnailConfig,
  speedLevel: SpeedLevel | null
): number {
  if (speedLevel === null) {
    return 1;
  }
  const configured = config.SPEED_GRID_STRIDE[speedLevel];
  const maxUsable = config.MAX_ACCEPTABLE_GAP * 2 + 1;
  if (configured < 1) {
    return 1;
  }
  return Math.min(configured, maxUsable);
}

/**
 * Directional batch size for the active speed level, derived from the
 * wall-clock look-ahead target rather than a fixed frame count.
 *
 * Outside a speed session the configured DIRECTIONAL_PREFETCH_BATCH is used
 * unchanged, so discrete skips keep their existing behaviour.
 *
 * @param config              - Active 10-foot configuration.
 * @param speedLevel          - Active speed level, or null outside a session.
 * @param thumbnailIntervalSec - Seconds between consecutive thumbnails.
 */
export function resolveDirectionalBatchSize(
  config: T10FootThumbnailConfig,
  speedLevel: SpeedLevel | null,
  thumbnailIntervalSec: number
): number {
  const configuredCap = config.DIRECTIONAL_PREFETCH_BATCH;
  if (speedLevel === null) {
    return configuredCap;
  }
  const contentSecondsNeeded =
    SPEED_VALUE[speedLevel] * config.LOOK_AHEAD_WALL_SECONDS;
  const frameSpan =
    thumbnailIntervalSec * resolveGridStride(config, speedLevel);
  if (frameSpan <= 0) {
    return configuredCap;
  }
  const needed = Math.ceil(contentSecondsNeeded / frameSpan);
  const floor = config.MIN_DIRECTIONAL_PREFETCH_BATCH;
  // The cap is applied LAST so it outranks the floor. Written the other way
  // round, a configured cap below the floor (a deliberately shallow test or
  // device profile) would be silently overridden by it.
  return Math.min(configuredCap, Math.max(floor, needed));
}

/**
 * Span, in content seconds, of one directional look-ahead batch at the current
 * speed. Single source of truth for the frontier arithmetic — the batch
 * builder, the frontier advance and the extension threshold must all agree, or
 * the frontier drifts and look-ahead either stalls or re-walks offsets already
 * queued.
 *
 * Deliberately composed from the same two resolvers the builder uses rather
 * than recomputing the product independently.
 *
 * @param config              - Active 10-foot configuration.
 * @param speedLevel          - Active speed level, or null outside a session.
 * @param thumbnailIntervalSec - Seconds between consecutive thumbnails.
 */
export function resolveDirectionalBatchSpan(
  config: T10FootThumbnailConfig,
  speedLevel: SpeedLevel | null,
  thumbnailIntervalSec: number
): number {
  return (
    resolveDirectionalBatchSize(config, speedLevel, thumbnailIntervalSec) *
    thumbnailIntervalSec *
    resolveGridStride(config, speedLevel)
  );
}

/**
 * Number of entries to evict in one pass: a batch while trick-play is active,
 * otherwise one (unchanged steady-state behaviour).
 *
 * The batch is clamped to a tenth of MAX_CACHE_SIZE so small configured caches
 * are not gutted — with MAX_CACHE_SIZE 300 this resolves to the configured 25,
 * while a test/low-memory cap of 5 or 12 resolves to 1.
 *
 * @param config            - Active 10-foot configuration.
 * @param isTrickPlayActive - Whether a scrub is currently in progress.
 */
export function resolveEvictionBatchSize(
  config: T10FootThumbnailConfig,
  isTrickPlayActive: boolean
): number {
  if (!isTrickPlayActive) {
    return 1;
  }
  const capRelativeLimit = Math.floor(
    config.MAX_CACHE_SIZE / EVICTION_BATCH_CAP_DIVISOR
  );
  return Math.max(1, Math.min(config.EVICTION_BATCH_SIZE, capRelativeLimit));
}
